import { Link } from 'gatsby';
import type React from 'react';

import { useLanguage } from '../../context/LanguageContext';
import styles from './Footer.module.css';

interface FooterProps {
  lang: 'de' | 'en';
}

const legalRoutes = {
  de: {
    impressum: '/impressum/',
    privacy: '/datenschutz/',
  },
  en: {
    impressum: '/en/legal-notice/',
    privacy: '/en/data-protection-information/',
  },
};

/**
 * Footer component.
 * Displays firm address, contact info, and legal page links.
 * Links and labels adapt to the active language.
 */
const Footer: React.FC<FooterProps> = ({ lang }) => {
  const { t } = useLanguage();

  const routes = legalRoutes[lang];

  return (
    <footer className={styles.footer} role="contentinfo">
      <div className={styles.container}>
        <div className={styles.info}>
          <p className={styles.firmName}>Minihold Rechtsanwälte GmbH</p>
          <address className={styles.address}>Julius-Tandler-Platz 6/4, 1090 Wien</address>
          <div className={styles.contact}>
            <a
              href="tel:+4315227200"
              className={styles.contactLink}
              aria-label={`${t('contact.phoneLabel')}: +43 1 522 72 00`}
            >
              +43 1 522 72 00
            </a>
            <a
              href="mailto:office@minihold.com"
              className={styles.contactLink}
              aria-label={`${t('contact.emailLabel')}: office@minihold.com`}
            >
              office@minihold.com
            </a>
          </div>
        </div>
        <nav
          className={styles.legal}
          aria-label={lang === 'de' ? 'Rechtliche Seiten' : 'Legal pages'}
        >
          <Link to={routes.impressum} className={styles.legalLink}>
            {t('footer.impressum')}
          </Link>
          <Link to={routes.privacy} className={styles.legalLink}>
            {t('footer.privacy')}
          </Link>
          <p>© {new Date().getFullYear()} Minihold Rechtsanwälte GmbH</p>
        </nav>
      </div>
    </footer>
  );
};

export default Footer;
